import { BorderAllIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/border-all-icon';
import { getTable } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/lib/tiptap-table-utils';
import { useTiptapEditor } from '@examdojo/core/tiptap-react-ui-components/hooks/use-tiptap-editor';
import { isExtensionAvailable } from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-utils';
import type { Editor } from '@tiptap/react';
import { useCallback } from 'react';

export interface UseTableBorderToggleConfig {
  /**
   * The Tiptap editor instance. If omitted, the hook will use
   * the editor from `useTiptapEditor`.
   */
  editor?: Editor | null;
  /**
   * Hide the button when the action isn't currently possible.
   * @default false
   */
  hideWhenUnavailable?: boolean;
  /**
   * Called after the border visibility is successfully toggled.
   */
  onToggled?: (bordersVisible: boolean) => void;
}

/**
 * Required Tiptap extensions for this feature to work.
 */
const REQUIRED_EXTENSIONS = ['table', 'tableHandleExtension'];

/**
 * Returns whether the table borders are currently visible
 */
function areBordersVisible(editor: Editor | null): boolean {
  if (!editor) return true; // Default to visible

  try {
    const table = getTable(editor);
    if (!table) return true;

    // Read the borderVisible attribute, defaulting to true if not set
    return table.node.attrs['borderVisible'] !== false;
  } catch {
    return true;
  }
}

/**
 * Returns whether border toggle action can run in the current state.
 */
function canToggleBorder(editor: Editor | null): boolean {
  if (!editor || !editor.isEditable || !isExtensionAvailable(editor, REQUIRED_EXTENSIONS)) {
    return false;
  }

  try {
    return editor.isActive('table') || editor.isActive('tableCell') || editor.isActive('tableHeader');
  } catch {
    return false;
  }
}

/**
 * Toggles the border visibility of the current table.
 */
function toggleTableBorder({ editor }: { editor: Editor | null }): boolean {
  if (!canToggleBorder(editor) || !editor) {
    return false;
  }

  try {
    const table = getTable(editor);
    if (!table) return false;

    const currentBorderVisible = table.node.attrs['borderVisible'] !== false;
    const newBorderVisible = !currentBorderVisible;

    const tr = editor.state.tr;
    tr.setNodeMarkup(table.pos, undefined, {
      ...table.node.attrs,
      borderVisible: newBorderVisible,
    });

    if (tr.docChanged) {
      editor.view.dispatch(tr);
    }

    return true;
  } catch (error) {
    console.error('Error toggling table border:', error);
    return false;
  }
}

/**
 * Determines whether a UI control should be visible.
 */
function shouldShowButton({
  editor,
  hideWhenUnavailable,
}: {
  editor: Editor | null;
  hideWhenUnavailable: boolean;
}): boolean {
  if (!editor || !editor.isEditable) return false;
  if (!isExtensionAvailable(editor, REQUIRED_EXTENSIONS)) return false;

  return hideWhenUnavailable ? canToggleBorder(editor) : true;
}

/**
 * React hook that provides a toggle for table border visibility.
 *
 * Returned API:
 * - `isVisible`: whether a button should be shown
 * - `canToggle`: whether the action can execute now
 * - `handleToggle()`: runs the action; returns `true` on success
 * - `bordersVisible`: current state of border visibility
 * - `label`: UI label
 * - `Icon`: a presentational icon component (changes based on state)
 */
export function useTableBorderToggle(config: UseTableBorderToggleConfig = {}) {
  const { editor: providedEditor, hideWhenUnavailable = false, onToggled } = config;

  const { editor } = useTiptapEditor(providedEditor);

  const isVisible = shouldShowButton({
    editor,
    hideWhenUnavailable,
  });

  const canPerformAction = canToggleBorder(editor);
  const bordersVisible = areBordersVisible(editor);

  const handleToggle = useCallback(() => {
    const success = toggleTableBorder({ editor });
    if (success) {
      const newBordersVisible = areBordersVisible(editor);
      onToggled?.(newBordersVisible);
    }
    return success;
  }, [editor, onToggled]);

  const label = bordersVisible ? 'Hide borders' : 'Show borders';
  const Icon = BorderAllIcon;

  return {
    isVisible,
    canToggle: canPerformAction,
    handleToggle,
    bordersVisible,
    label,
    Icon,
  };
}
